import type { FetchApi } from '@backstage/core-plugin-api';
import type { SreDashboardApi } from './SreDashboardApi';
import type {
  PaginatedResult,
  IncidentId,
  ExecutionId,
  TicketId,
  Incident,
  IncidentFilter,
  ExecutionSummary,
  ExecutionDetail,
  ExecutionEvent,
  SLODefinition,
  SLODetail,
  Postmortem,
  PostmortemFilter,
  Checklist,
  CheckItem,
  KaizenItem,
  MonthlyReport,
  PlatformRole,
  PlatformRoleName,
  UserRoleAssignment,
  AuditLog,
  AuditLogFilter,
  AuditExportFormat,
  ResourceExclusion,
  CreateExclusionRequest,
  ExclusionFilter,
  DecisionSearchFilter,
  ChangeSearchFilter,
  DecisionLog,
  ChangeLog,
  SimilarDecision,
  TimelineEntry,
  Notification,
  ActivityFeedItem,
  Ticket,
  TicketFilter,
  CreateTicketRequest,
  UpdateTicketRequest,
} from '../types';
import { MOCK_INCIDENTS, MOCK_SLOS, MOCK_POSTMORTEMS, MOCK_CHECKLIST, MOCK_KAIZEN_ITEMS, MOCK_ROLES, MOCK_ASSIGNMENTS, MOCK_AUDIT_LOGS, MOCK_EXCLUSIONS } from './mockData';
import { MOCK_NOTIFICATIONS, MOCK_ACTIVITY_FEED } from './mockNotifications';
import { MOCK_TICKETS } from './mockTickets';

const PROXY_BASE = '/api/proxy/superagent-sre';

/**
 * SRE Dashboard API Client
 *
 * Communicates with superagent-sre through the Backstage backend proxy.
 * Falls back to mock data when the backend is unavailable (MVP/demo mode).
 * Implements all 25 API methods with FetchApi DI.
 */
export class SreDashboardClient implements SreDashboardApi {
  private readonly fetchApi: FetchApi;
  private useMockData = false;

  constructor(options: { readonly fetchApi: FetchApi }) {
    this.fetchApi = options.fetchApi;
  }

  // ── Private helpers ────────────────────────────────────────

  private buildUrl(path: string, params?: Record<string, string | undefined>): string {
    const url = `${PROXY_BASE}${path}`;
    if (!params) return url;
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, value);
      }
    }
    const query = searchParams.toString();
    return query ? `${url}?${query}` : url;
  }

  private async get<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
    if (this.useMockData) {
      throw new Error('Mock mode');
    }
    const url = this.buildUrl(path, params);
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      this.useMockData = true;
      throw new Error('Backend returned non-JSON response (proxy unavailable)');
    }
    return response.json() as Promise<T>;
  }

  private async post<T>(path: string, body?: unknown): Promise<T> {
    const response = await this.fetchApi.fetch(`${PROXY_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return response.json() as Promise<T>;
  }

  private async del(path: string): Promise<void> {
    const response = await this.fetchApi.fetch(`${PROXY_BASE}${path}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
  }

  // ── Incidents ──────────────────────────────────────────────

  async getIncidents(filter?: IncidentFilter): Promise<PaginatedResult<Incident>> {
    try {
      return await this.get<PaginatedResult<Incident>>('/api/sre/incidents', {
        status: filter?.status,
        severity: filter?.severity,
        source: filter?.source,
        search: filter?.search,
        limit: filter?.limit?.toString(),
        offset: filter?.offset?.toString(),
      });
    } catch {
      let data: readonly Incident[] = MOCK_INCIDENTS;
      if (filter?.status) data = data.filter(i => i.status === filter.status);
      if (filter?.severity) data = data.filter(i => i.severity === filter.severity);
      if (filter?.search) {
        const q = filter.search.toLowerCase();
        data = data.filter(i => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
      }
      return { data, total: data.length };
    }
  }

  async getIncident(id: IncidentId): Promise<Incident | null> {
    try {
      return await this.get<Incident>(`/api/sre/incidents/${id}`);
    } catch {
      return MOCK_INCIDENTS.find(i => i.id === id) ?? null;
    }
  }

  // ── Runbook Execution ──────────────────────────────────────

  async startRunbook(incidentId: IncidentId): Promise<ExecutionSummary> {
    return this.post<ExecutionSummary>(`/api/sre/incidents/${incidentId}/runbook/start`);
  }

  async getExecution(execId: ExecutionId): Promise<ExecutionDetail> {
    return this.get<ExecutionDetail>(`/api/sre/executions/${execId}`);
  }

  async approveStep(execId: ExecutionId, stepName: string): Promise<void> {
    await this.post(`/api/sre/executions/${execId}/steps/${stepName}/approve`);
  }

  async denyStep(execId: ExecutionId, stepName: string, reason: string): Promise<void> {
    await this.post(`/api/sre/executions/${execId}/steps/${stepName}/deny`, { reason });
  }

  streamExecution(execId: ExecutionId, onEvent: (event: ExecutionEvent) => void): () => void {
    const url = `${PROXY_BASE}/api/sre/executions/${execId}/stream`;
    const eventSource = new EventSource(url);
    eventSource.onmessage = (e: MessageEvent) => {
      try {
        const event = JSON.parse(e.data as string) as ExecutionEvent;
        onEvent(event);
      } catch {
        // Skip malformed events
      }
    };
    return () => eventSource.close();
  }

  // ── SLO ────────────────────────────────────────────────────

  async getSLOs(): Promise<readonly SLODefinition[]> {
    try {
      const result = await this.get<{ data: readonly SLODefinition[] }>('/api/sre/slos');
      return result.data;
    } catch {
      return MOCK_SLOS;
    }
  }

  async getSLODetail(sloId: string): Promise<SLODetail | null> {
    try {
      return await this.get<SLODetail>(`/api/sre/slos/${sloId}`);
    } catch {
      return null;
    }
  }

  // ── Postmortem ─────────────────────────────────────────────

  async getPostmortems(filter?: PostmortemFilter): Promise<PaginatedResult<Postmortem>> {
    try {
      return await this.get<PaginatedResult<Postmortem>>('/api/sre/postmortems', {
        status: filter?.status,
        search: filter?.search,
        limit: filter?.limit?.toString(),
        offset: filter?.offset?.toString(),
      });
    } catch {
      let data: readonly Postmortem[] = MOCK_POSTMORTEMS;
      if (filter?.status) data = data.filter(p => p.status === filter.status);
      return { data, total: data.length };
    }
  }

  async createPostmortem(incidentId: IncidentId): Promise<Postmortem> {
    return this.post<Postmortem>('/api/sre/postmortems', { incidentId });
  }

  // ── DailyOps ───────────────────────────────────────────────

  async getChecklist(_date: string): Promise<Checklist> {
    try {
      return await this.get<Checklist>('/api/sre/daily-ops/checklist', { date: _date });
    } catch {
      return MOCK_CHECKLIST;
    }
  }

  async toggleCheckItem(itemId: string): Promise<CheckItem> {
    return this.post<CheckItem>(`/api/sre/daily-ops/checklist/items/${itemId}/toggle`);
  }

  async getKaizenItems(): Promise<readonly KaizenItem[]> {
    try {
      const result = await this.get<{ data: readonly KaizenItem[] }>('/api/sre/daily-ops/kaizen');
      return result.data;
    } catch {
      return MOCK_KAIZEN_ITEMS;
    }
  }

  async getMonthlyReport(month: string): Promise<MonthlyReport | null> {
    try {
      return await this.get<MonthlyReport>('/api/sre/reports/monthly', { month });
    } catch {
      return null;
    }
  }

  // ── Governance ─────────────────────────────────────────────

  async getRoles(): Promise<readonly PlatformRole[]> {
    try {
      const result = await this.get<{ data: readonly PlatformRole[] }>('/api/sre/governance/roles');
      return result.data;
    } catch {
      return MOCK_ROLES;
    }
  }

  async getAssignments(): Promise<readonly UserRoleAssignment[]> {
    try {
      const result = await this.get<{ data: readonly UserRoleAssignment[] }>('/api/sre/governance/assignments');
      return result.data;
    } catch {
      return MOCK_ASSIGNMENTS;
    }
  }

  async assignRole(userId: string, roleName: PlatformRoleName): Promise<UserRoleAssignment> {
    return this.post<UserRoleAssignment>('/api/sre/governance/assignments', { userId, roleName });
  }

  async revokeRole(assignmentId: string): Promise<void> {
    await this.del(`/api/sre/governance/assignments/${assignmentId}`);
  }

  async getAuditLogs(filter?: AuditLogFilter): Promise<PaginatedResult<AuditLog>> {
    try {
      return await this.get<PaginatedResult<AuditLog>>('/api/sre/governance/audit-logs', {
        operator: filter?.operator,
        operationType: filter?.operationType,
        result: filter?.result,
        from: filter?.from,
        to: filter?.to,
        limit: filter?.limit?.toString(),
        offset: filter?.offset?.toString(),
      });
    } catch {
      return { data: MOCK_AUDIT_LOGS, total: MOCK_AUDIT_LOGS.length };
    }
  }

  async exportAuditLogs(filter: AuditLogFilter, format: AuditExportFormat): Promise<string> {
    const response = await this.fetchApi.fetch(
      this.buildUrl('/api/sre/governance/audit-logs/export', {
        ...this.filterToParams(filter),
        format,
      }),
    );
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return response.text();
  }

  // ── Exclusions ─────────────────────────────────────────────

  async getExclusions(filter?: ExclusionFilter): Promise<PaginatedResult<ResourceExclusion>> {
    try {
      return await this.get<PaginatedResult<ResourceExclusion>>('/api/sre/exclusions', {
        resourceId: filter?.resourceId,
        isActive: filter?.isActive?.toString(),
        search: filter?.search,
        limit: filter?.limit?.toString(),
        offset: filter?.offset?.toString(),
      });
    } catch {
      return { data: MOCK_EXCLUSIONS, total: MOCK_EXCLUSIONS.length };
    }
  }

  async createExclusion(req: CreateExclusionRequest): Promise<ResourceExclusion> {
    return this.post<ResourceExclusion>('/api/sre/exclusions', req);
  }

  async deleteExclusion(id: string): Promise<void> {
    await this.del(`/api/sre/exclusions/${id}`);
  }

  // ── History ────────────────────────────────────────────────

  async searchDecisions(filter: DecisionSearchFilter): Promise<PaginatedResult<DecisionLog>> {
    try {
      return await this.get<PaginatedResult<DecisionLog>>('/api/sre/decisions', {
        keyword: filter.keyword,
        incidentId: filter.incidentId,
        decision: filter.decision,
        decidedBy: filter.decidedBy,
        from: filter.from,
        to: filter.to,
        limit: filter.limit?.toString(),
        offset: filter.offset?.toString(),
      });
    } catch {
      return { data: [], total: 0 };
    }
  }

  async searchChanges(filter: ChangeSearchFilter): Promise<PaginatedResult<ChangeLog>> {
    try {
      return await this.get<PaginatedResult<ChangeLog>>('/api/sre/changes', {
        incidentId: filter.incidentId,
        targetResource: filter.targetResource,
        changeType: filter.changeType,
        executedBy: filter.executedBy,
        result: filter.result,
        from: filter.from,
        to: filter.to,
        limit: filter.limit?.toString(),
        offset: filter.offset?.toString(),
      });
    } catch {
      return { data: [], total: 0 };
    }
  }

  async findSimilarDecisions(incidentId: IncidentId): Promise<readonly SimilarDecision[]> {
    try {
      const result = await this.get<{ data: readonly SimilarDecision[] }>(
        `/api/sre/decisions/similar/${incidentId}`,
      );
      return result.data;
    } catch {
      return [];
    }
  }

  async getTimeline(incidentId: IncidentId): Promise<readonly TimelineEntry[]> {
    try {
      const result = await this.get<{ data: readonly TimelineEntry[] }>(
        `/api/sre/history/timeline/${incidentId}`,
      );
      return result.data;
    } catch {
      return [];
    }
  }

  // ── Notifications ─────────────────────────────────────────

  async getNotifications(): Promise<readonly Notification[]> {
    try {
      const result = await this.get<{ data: readonly Notification[] }>(
        '/api/sre/notifications',
      );
      return result.data;
    } catch {
      return MOCK_NOTIFICATIONS;
    }
  }

  async getActivityFeed(): Promise<readonly ActivityFeedItem[]> {
    try {
      const result = await this.get<{ data: readonly ActivityFeedItem[] }>(
        '/api/sre/activity-feed',
      );
      return result.data;
    } catch {
      return MOCK_ACTIVITY_FEED;
    }
  }

  // ── Tickets ──────────────────────────────────────────────────

  async getTickets(filter?: TicketFilter): Promise<PaginatedResult<Ticket>> {
    try {
      return await this.get<PaginatedResult<Ticket>>('/api/sre/tickets', {
        status: filter?.status,
        priority: filter?.priority,
        category: filter?.category,
        assignee: filter?.assignee,
        search: filter?.search,
        limit: filter?.limit?.toString(),
        offset: filter?.offset?.toString(),
      });
    } catch {
      let data: readonly Ticket[] = MOCK_TICKETS;
      if (filter?.status) data = data.filter(t => t.status === filter.status);
      if (filter?.priority) data = data.filter(t => t.priority === filter.priority);
      if (filter?.category) data = data.filter(t => t.category === filter.category);
      if (filter?.search) {
        const q = filter.search.toLowerCase();
        data = data.filter(t => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
      }
      return { data, total: data.length };
    }
  }

  async getTicket(id: TicketId): Promise<Ticket | null> {
    try {
      return await this.get<Ticket>(`/api/sre/tickets/${id}`);
    } catch {
      return MOCK_TICKETS.find(t => t.id === id) ?? null;
    }
  }

  async createTicket(req: CreateTicketRequest): Promise<Ticket> {
    try {
      return await this.post<Ticket>('/api/sre/tickets', req);
    } catch {
      // Mock: create a local ticket for demo
      const now = new Date().toISOString();
      const nextNum = MOCK_TICKETS.length + 1;
      return {
        id: `tkt-new-${nextNum}` as unknown as TicketId,
        ticketNumber: `TKT-2026-${String(nextNum).padStart(4, '0')}`,
        title: req.title,
        description: req.description,
        status: 'open',
        priority: req.priority,
        category: req.category,
        assignee: req.assignee ?? null,
        reporter: 'デモユーザー',
        relatedIncidentId: (req.relatedIncidentId ?? null) as Ticket['relatedIncidentId'],
        tags: req.tags ?? [],
        comments: [],
        dueDate: req.dueDate ?? null,
        createdAt: now,
        updatedAt: now,
        resolvedAt: null,
      };
    }
  }

  async updateTicket(id: TicketId, req: UpdateTicketRequest): Promise<Ticket> {
    try {
      return await this.post<Ticket>(`/api/sre/tickets/${id}`, req);
    } catch {
      const ticket = MOCK_TICKETS.find(t => t.id === id);
      if (!ticket) throw new Error(`Ticket not found: ${id}`);
      return { ...ticket, ...req, updatedAt: new Date().toISOString() } as Ticket;
    }
  }

  async addTicketComment(id: TicketId, content: string): Promise<Ticket> {
    try {
      return await this.post<Ticket>(`/api/sre/tickets/${id}/comments`, { content });
    } catch {
      const ticket = MOCK_TICKETS.find(t => t.id === id);
      if (!ticket) throw new Error(`Ticket not found: ${id}`);
      return {
        ...ticket,
        comments: [
          ...ticket.comments,
          { id: `cmt-new-${Date.now()}`, author: 'デモユーザー', content, createdAt: new Date().toISOString() },
        ],
        updatedAt: new Date().toISOString(),
      };
    }
  }

  // ── Internal helpers ───────────────────────────────────────

  private filterToParams(filter: AuditLogFilter): Record<string, string | undefined> {
    return {
      operator: filter.operator,
      operationType: filter.operationType,
      result: filter.result,
      from: filter.from,
      to: filter.to,
      limit: filter.limit?.toString(),
      offset: filter.offset?.toString(),
    };
  }
}
